<script lang="ts">
import { accuracy, computeStreak, dailyCounts, deckAccuracy } from '$lib/stats';
import type { ReviewLogEntry } from '$lib/store';
import type { Card } from '$types/card';

interface Props {
  cards: readonly Card[];
  log: readonly ReviewLogEntry[];
}

const { cards, log }: Props = $props();

const days = $derived(dailyCounts(log, 84)); // 12 Wochen
const deckOf = $derived.by(() => {
  const m = new Map(cards.map((c) => [c.id, c.deck]));
  return (id: string) => m.get(id);
});
const decks = $derived(deckAccuracy(log, deckOf));
const weakest = $derived(decks.find((d) => d.reviews >= 5) ?? null);
const acc = $derived(Math.round(accuracy(log) * 100));
const streak = $derived(computeStreak(log));

function level(count: number): number {
  if (count === 0) return 0;
  if (count < 5) return 1;
  if (count < 15) return 2;
  if (count < 30) return 3;
  return 4;
}
</script>

<section class="stats">
  <div class="rule"><span class="rule-label">[ STATISTIK ]</span><span class="rule-line"></span></div>

  <div class="kpi-row">
    <div class="kpi"><span class="kpi-k">reviews gesamt</span><span class="kpi-v">{log.length}</span></div>
    <div class="kpi"><span class="kpi-k">trefferquote</span><span class="kpi-v">{acc} %</span></div>
    <div class="kpi"><span class="kpi-k">streak</span><span class="kpi-v">{streak} d</span></div>
  </div>

  <div class="rule"><span class="rule-label">[ 12-WOCHEN-HEATMAP ]</span><span class="rule-line"></span></div>
  <div class="heatmap" role="img" aria-label="Reviews pro Tag, letzte 12 Wochen">
    {#each days as d}
      <div class="cell l{level(d.count)}" title="{d.date}: {d.count} reviews"></div>
    {/each}
  </div>
  <div class="legend">
    <span>weniger</span>
    {#each [0, 1, 2, 3, 4] as l}<div class="cell l{l}"></div>{/each}
    <span>mehr</span>
  </div>

  <div class="rule"><span class="rule-label">[ TREFFERQUOTE JE DECK — schwächste zuerst ]</span><span class="rule-line"></span></div>
  {#if decks.length === 0}
    <p class="hint">Noch keine Reviews — Statistik füllt sich beim Lernen.</p>
  {:else}
    <table class="deck-table">
      <thead><tr><th>deck</th><th>reviews</th><th>quote</th></tr></thead>
      <tbody>
        {#each decks as d}
          <tr class:weak={d.accuracy < 0.85 && d.reviews >= 5}>
            <td>{d.deck}</td>
            <td>{d.reviews}</td>
            <td>{Math.round(d.accuracy * 100)} %</td>
          </tr>
        {/each}
      </tbody>
    </table>
    {#if weakest && weakest.accuracy < 0.85}
      <p class="drill-hint">
        → Drill-Empfehlung: <b>{weakest.deck}</b> ({Math.round(weakest.accuracy * 100)} %) —
        im Lern-Modus als Lernfeld-Filter setzen.
      </p>
    {/if}
  {/if}
</section>

<style>
  .stats { margin-top: 8px; }
  .rule {
    display: flex; align-items: center; gap: 10px;
    margin: 18px 0 8px;
    font-size: 11px; letter-spacing: 0.08em;
    color: var(--ink-3); text-transform: uppercase;
  }
  .rule-label { white-space: nowrap; font-weight: 600; }
  .rule-line { flex: 1; height: 0; border-top: 1px dashed var(--rule); opacity: 0.5; }
  .hint { font-size: 13px; color: var(--ink-2); }

  .kpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .kpi {
    border: 2px solid var(--ink); background: var(--paper);
    padding: 10px 12px; display: flex; flex-direction: column; gap: 2px;
    box-shadow: 4px 4px 0 var(--ink-3);
  }
  .kpi-k { font-size: 9px; letter-spacing: 0.14em; color: var(--muted); text-transform: uppercase; }
  .kpi-v { font-size: 22px; font-weight: 800; font-variant-numeric: tabular-nums; }

  .heatmap {
    display: grid;
    grid-template-rows: repeat(7, 12px);
    grid-auto-flow: column;
    grid-auto-columns: 12px;
    gap: 3px;
    overflow-x: auto;
    padding-bottom: 4px;
  }
  .cell { width: 12px; height: 12px; background: var(--paper-3); }
  .cell.l1 { background: color-mix(in srgb, var(--accent) 25%, var(--paper-3)); }
  .cell.l2 { background: color-mix(in srgb, var(--accent) 50%, var(--paper-3)); }
  .cell.l3 { background: color-mix(in srgb, var(--accent) 75%, var(--paper-3)); }
  .cell.l4 { background: var(--accent); }
  .legend { display: flex; align-items: center; gap: 4px; margin-top: 6px; font-size: 10px; color: var(--muted); }

  .deck-table { width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums; }
  .deck-table th {
    text-align: left; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--muted); padding: 4px 8px; border-bottom: 1px solid var(--rule);
  }
  .deck-table td { padding: 5px 8px; border-bottom: 1px dashed var(--rule); }
  .deck-table tr.weak td { color: var(--r-again, #c62828); font-weight: 600; }
  .drill-hint { font-size: 13px; margin-top: 10px; color: var(--ink-2); }
</style>
